import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

// Components
import { Button } from '@/components/v4/index';
import FormBase from '@/components/forms/new/FormBase';
import SelectiveSyncToggle from './formFields/SelectiveSyncToggle';
import GroupNameField from './formFields/GroupNameField';
import UsageLocationsField from './formFields/UsageLocationsField';

import iso3166CountryCodes from '../../../../utils/iso3166CountryCodes';
import { chakraSelectDarkMode, defaultChakraSelectStyle } from '@/constants';
import walkthroughId from '@/pages/services/walkthroughIds';

export default function SelectiveSyncForm({
    selectedGroupName,
    selectedGroupLocations,
    setSelectedGroupLocations,
    setSelectedGroupName,
    serviceWizard,
    isLoading,
    edit,
}) {
    const countryOptions = iso3166CountryCodes.map((country) => ({
        label: country.name,
        value: country['alpha-2'],
    }));
    const { darkMode } = useSelector((state) => state.settings);
    const chakraSelectDark = chakraSelectDarkMode(darkMode);
    const chakraStyles = { ...defaultChakraSelectStyle, ...chakraSelectDark };
    const { ufGroupNameInput, ufUsageLocationTags, ufUpdateButton } =
        walkthroughId.selectiveSync || {};

    /**
     * Validate any field that is enabled (checkbox is checked)
     */

    /**
     * Reset the selective sync form whenever the checkboxes are toggled.
     */
    useEffect(() => {
        if (!selectedGroupName && !edit) {
            serviceWizard.setFilterGroup('');
        }
        if (!selectedGroupLocations && !edit) {
            serviceWizard.setFilterLocations(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupName, selectedGroupLocations]);

    /**
     * Set any edit data.
     */
    useEffect(() => {
        if (serviceWizard.selectiveSyncEditData?.id) {
            if (serviceWizard.selectiveSyncEditData?.filterGroup) {
                setSelectedGroupName(true);
                serviceWizard.setFilterGroup(
                    serviceWizard.selectiveSyncEditData?.filterGroup,
                );
            }
            if (
                serviceWizard.selectiveSyncEditData?.filterLocations[0]?.length
            ) {
                let items = [];
                iso3166CountryCodes.filter((item) => {
                    const matchFn = (element) => element === item['alpha-2'];
                    const match =
                        serviceWizard.selectiveSyncEditData?.filterLocations.some(
                            matchFn,
                        );
                    if (match) {
                        items.push({
                            label: item.name,
                            value: item['alpha-2'],
                        });
                    }
                });

                setSelectedGroupLocations(true);
                serviceWizard.setFilterLocations(items);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceWizard.selectiveSyncEditData]);

    const initialValues = {
        filterGroup: serviceWizard.filterGroup || '',
        filterLocations: serviceWizard.filterLocations || [],
    };

    const finalFormSubmit = () => {
        if (edit) {
            serviceWizard.onSelectiveSyncEdit();
        } else {
            serviceWizard.onSelectiveSync();
        }
    };

    const formFields = (errors, touched, values) => {
        const fields = [
            <SelectiveSyncToggle
                key="selectiveSyncToggle"
                darkMode={darkMode}
                serviceWizard={serviceWizard}
            />,
            <GroupNameField
                key="groupNameField"
                darkMode={darkMode}
                serviceWizard={serviceWizard}
                walkthroughId={ufGroupNameInput}
                errors={errors}
                touched={touched}
                values={values}
            />,
            <UsageLocationsField
                key="usageLocationsField"
                darkMode={darkMode}
                serviceWizard={serviceWizard}
                options={countryOptions}
                chakraStyles={chakraStyles}
                walkthroughId={ufUsageLocationTags}
                errors={errors}
                touched={touched}
                values={values}
            />,
        ];

        return fields;
    };

    const extraButton = (
        <Box display="flex" gap={3}>
            {edit &&
                serviceWizard?.company?.selectiveSyncPolicy !== 2 &&
                serviceWizard?.msServiceAuth?.selectiveSync && (
                    <Button
                        colorScheme="red"
                        onClick={() => serviceWizard.onSelectiveSyncDisable()}>
                        Disable Selective Sync
                    </Button>
                )}
        </Box>
    );
    const hasGroupName = Boolean(serviceWizard.filterGroup?.trim());
    const hasUsageLocations = Boolean(serviceWizard.filterLocations?.length);
    const isSubmitDisabled =
        (!hasGroupName && !hasUsageLocations) ||
        serviceWizard.selectiveSyncToggle === false ||
        isLoading;
    return (
        <FormBase
            header="Select Sync Parameters"
            initialValues={initialValues}
            finalFormSubmit={finalFormSubmit}
            noBackBtn
            hideSubmitButton={false}
            submitButtonText={edit ? 'Update' : 'Next'}
            isSubmitDisabled={isSubmitDisabled}
            enableReinitialize={true}
            extraButton={extraButton}
            ufSaveBtn={`${ufUpdateButton}/${edit ? 'Update' : 'Next'}`}>
            {({ errors, touched, values }) =>
                formFields(errors, touched, values)
            }
        </FormBase>
    );
}
